import CloseIcon from "@mui/icons-material/Close";
import Download from "@mui/icons-material/Download";
import {
  Box,
  CircularProgress,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { SpecialZoomLevel } from "@react-pdf-viewer/core";
import { FilePreview } from "components/Document/shared/FilePreview";
import { useDocumentDownload } from "hooks/useDocumentDownload";
import { useDocumentDownloadLink } from "hooks/useDocumentDownloadLink";
import { useCallback, useEffect, useState } from "react";

interface ManifestPdfRailProps {
  /** The PDF document currently targeted for preview. */
  document: { id: string; title: string; fileExtension?: string | null };
  onClose: () => void;
}

/**
 * Right-hand rail that previews the manifest PDF alongside the scrolling detail
 * sections. Opened on demand (the Documents "eye" action) and closed with the
 * header's X — there's no persistent minimized strip. Fetches a fresh signed
 * URL whenever the targeted document changes. The PDF viewer brings its own
 * border + toolbar, so the rail adds no extra chrome around it.
 */
export function ManifestPdfRail({ document, onClose }: ManifestPdfRailProps) {
  const [getDownloadLink, { data, loading }] = useDocumentDownloadLink();
  const { fetchAndHandleDownload } = useDocumentDownload();
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    void getDownloadLink({ variables: { id: document.id } });
  }, [document.id, getDownloadLink]);

  const fileUrl = data?.getDocumentDownloadLink;

  // Save the source PDF so reviewers can open it on a larger screen. Reuses the
  // shared download hook (fresh signed link + real filename) rather than the
  // preview's signed URL above.
  const handleDownload = useCallback(async () => {
    setIsDownloading(true);
    try {
      await fetchAndHandleDownload({ documentId: document.id });
    } finally {
      setIsDownloading(false);
    }
  }, [document.id, fetchAndHandleDownload]);

  return (
    <Box
      sx={{
        // Fills the (width-animated) rail wrapper, which is ~40% of the drawer.
        width: "100%",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ px: 1.5, py: 1 }}
      >
        <Typography variant="subtitle2" color="text.secondary" noWrap title={document.title}>
          {document.title}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Tooltip title="Download document" placement="left">
            {/* span wrapper lets the tooltip work while the button is disabled mid-download */}
            <span>
              <IconButton
                size="small"
                onClick={handleDownload}
                disabled={isDownloading}
                aria-label="Download document"
              >
                {isDownloading ? <CircularProgress size={20} /> : <Download />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Hide manifest PDF" placement="left">
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {loading || !fileUrl ? (
          <Skeleton variant="rectangular" animation="wave" height="100%" width="100%" />
        ) : (
          <FilePreview
            fileUrl={fileUrl}
            fileExtension={document.fileExtension ?? "pdf"}
            defaultScale={SpecialZoomLevel.PageFit}
          />
        )}
      </Box>
    </Box>
  );
}
